import React, { useState, useMemo } from 'react';
import { useLang } from '@/context/LanguageContext';
import { ALL_ITEMS, CANCELLED, FESTIVAL_DAYS, disciplinesFor, formatDay } from '@/data/catalog';
import { ShowCard } from '@/components/ShowCard';
import { Avis } from '@/components/Avis';
import { Search, SlidersHorizontal, X } from 'lucide-react';

// showDates={false} deja la agenda sin nada temporal: ni filtro por día, ni
// fecha ni hora en las fichas. Es como se ve en la página de una edición ya
// pasada, donde el día concreto de cada pase ya no le sirve a nadie.
export const Agenda = ({ onOpenShow, showHeader = true, showDates = true }) => {
  const { t, lang } = useLang();
  const [query, setQuery] = useState('');
  const [day, setDay] = useState('all');
  const [discipline, setDiscipline] = useState('all');
  const [type, setType] = useState('all');

  const disciplines = useMemo(() => disciplinesFor(lang), [lang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ALL_ITEMS.filter((s) => {
      if (type === 'shows' && s.tipus !== 'espectacle') return false;
      if (type === 'install' && s.tipus !== 'installacio') return false;
      if (showDates && day !== 'all' && s.dia !== day && !(s.dies && s.dies.includes(day))) return false;
      if (discipline !== 'all' && s.disciplina[lang] !== discipline) return false;
      if (q) {
        const hay = `${s.titol} ${s.compania} ${s.disciplina[lang]} ${s.pais[lang]} ${s.ubicacio}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [query, day, discipline, type, lang, showDates]);

  const hasFilters = query || (showDates && day !== 'all') || discipline !== 'all' || type !== 'all';
  const clearAll = () => { setQuery(''); setDay('all'); setDiscipline('all'); setType('all'); };

  const Pill = ({ active, onClick, children, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors duration-200 ${
        active
          ? 'border-[var(--cea-accent)] bg-[var(--cea-accent)] text-[#040f0c]'
          : 'border-[var(--cea-border)] bg-[#0b241b] text-white/75 hover:border-[var(--cea-border-strong)]'
      }`}
    >{children}</button>
  );

  return (
    <section id="agenda" data-testid="agenda-section" className="relative scroll-mt-20 py-20 sm:py-28 bg-[#7c827e]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {showHeader && (
          <div className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.18em] text-[var(--cea-accent)] uppercase">{t.agenda.tag}</span>
            <h2 className="mt-3 font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em]">{t.agenda.title}</h2>
            <p className="mt-4 text-white/70">{t.agenda.subtitle}</p>
          </div>
        )}

        {/* Avisos de última hora (se autogestionan desde festivalData.json) */}
        {CANCELLED.length > 0 && (
          <div className="mt-8 max-w-3xl">
            <Avis variant="full" />
          </div>
        )}

        {/* Filters */}
        <div className={`${showHeader ? 'mt-8' : ''} rounded-2xl border border-[var(--cea-border)] bg-[#0b241b]/60 p-4 sm:p-5 space-y-4`}>
          {/* search + type */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="agenda-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.agenda.search}
                className="w-full rounded-xl border border-[var(--cea-border)] bg-[#040f0c] pl-11 pr-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[var(--cea-accent)]"
              />
            </div>
            <div className="flex gap-2">
              <Pill testid="agenda-type-all" active={type === 'all'} onClick={() => setType('all')}>{t.agenda.typeAll}</Pill>
              <Pill testid="agenda-type-shows" active={type === 'shows'} onClick={() => setType('shows')}>{t.agenda.typeShows}</Pill>
              <Pill testid="agenda-type-install" active={type === 'install'} onClick={() => setType('install')}>{t.agenda.typeInstall}</Pill>
            </div>
          </div>

          {/* date pills */}
          {showDates && (
            <div>
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="agenda-date-filters">
                <Pill testid="agenda-date-all" active={day === 'all'} onClick={() => setDay('all')}>{t.agenda.allDates}</Pill>
                {FESTIVAL_DAYS.map((d) => (
                  <Pill key={d} testid={`agenda-date-${d}`} active={day === d} onClick={() => setDay(d)}>{formatDay(d, t.months)}</Pill>
                ))}
              </div>
            </div>
          )}

          {/* discipline pills */}
          <div className="flex items-start gap-2">
            <SlidersHorizontal size={16} className="mt-2 text-white/40 flex-shrink-0" />
            <div className="flex flex-wrap gap-2" data-testid="agenda-discipline-filters">
              <Pill testid="agenda-discipline-all" active={discipline === 'all'} onClick={() => setDiscipline('all')}>{t.agenda.allDisciplines}</Pill>
              {disciplines.map((d) => (
                <Pill key={d} testid={`agenda-discipline-${d}`} active={discipline === d} onClick={() => setDiscipline(d)}>{d}</Pill>
              ))}
            </div>
          </div>
        </div>

        {/* results header */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-white/60" data-testid="agenda-results-count">
            <span className="font-bold text-white">{filtered.length}</span> {t.agenda.results}
          </p>
          {hasFilters && (
            <button data-testid="agenda-clear-filters" onClick={clearAll} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--cea-accent)] hover:underline">
              <X size={14} /> {t.agenda.clear}
            </button>
          )}
        </div>

        {/* grid */}
        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agenda-grid">
            {filtered.map((s, i) => (
              <ShowCard key={s.id} show={s} index={i} onOpen={onOpenShow} showDates={showDates} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-2xl border border-dashed border-[var(--cea-border)] bg-[#0b241b]/40 p-12 text-center" data-testid="agenda-no-results">
            <p className="text-white/60">{t.agenda.noResults}</p>
            <button onClick={clearAll} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--cea-accent)] hover:underline">
              <X size={14} /> {t.agenda.clear}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
